import { useState, useMemo } from 'react';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import type { WhatToExpectItem, EnrollmentStatus } from 'features/resources/types';
import { EnrollmentDialogState } from 'features/resources/types';
import { isFetchBaseQueryError } from 'utils';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import { useCareProgramModalRoute } from './useCareProgramModalRoute';
import { useCareProgramsData } from './useCareProgramsData';
import { transformCareProgramToDetail } from '../utils/careProgramTransformers';
import { deriveEnrollmentStatus } from '../utils/enrollmentUtils';
import { useCareProgramEnrollment } from './useCareProgramEnrollment';

export interface UseCareProgramRequestProps {
  enrollmentRequested?: boolean;
  onConfirmEnroll?: () => Promise<void>;
  onOpenChat?: () => void;
  defaultDialogState?: EnrollmentDialogState;
  enrollmentStatus?: EnrollmentStatus;
  programId?: number;
  onRefetchCarePrograms?: () => void;
}

/**
 * Owns the care-programs data fetch (independently of useCareProgramsByTopic,
 * which fetches its own copy for topic/section grouping) and everything
 * downstream of program selection: the selected program (via the `programId`
 * URL param), its derived enrollment status, the request-to-enroll dialog
 * flow, and the open-chat handoff. Self-sufficient with no args — e.g. a
 * caller that lands directly on a `?programId=` URL with no topic context.
 * Callers that already have a resolved program (e.g. CareProgramDetailPage
 * rendered with an explicit `program` prop for Storybook) can override
 * `programId`, `enrollmentStatus`, and `enrollmentRequested` directly.
 */
export const useCareProgramRequest = ({
  enrollmentRequested: enrollmentRequestedProp,
  onConfirmEnroll,
  onOpenChat,
  defaultDialogState = EnrollmentDialogState.NONE,
  enrollmentStatus: enrollmentStatusProp,
  programId: programIdProp,
  onRefetchCarePrograms,
}: UseCareProgramRequestProps = {}) => {
  const { isMobile, isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const user = useLoggedInUser();
  const navigate = useNavigate();
  const {
    careProgramsData,
    isLoading,
    error,
    refetch: refetchCarePrograms,
  } = useCareProgramsData();
  const { trackEvent } = usePendo();
  const { selectedProgram, isModalOpen, openProgram, closeModal } =
    useCareProgramModalRoute(careProgramsData);
  const programDetail = selectedProgram ? transformCareProgramToDetail(selectedProgram) : null;
  const enrollmentStatus = selectedProgram ? deriveEnrollmentStatus(selectedProgram) : null;

  const { requestEnrollment, isSubmitting } = useCareProgramEnrollment();

  const programId = programIdProp ?? programDetail?.programId;
  const enrollmentRequested =
    enrollmentRequestedProp ??
    (enrollmentStatus ? enrollmentStatus.hasRequestPending || enrollmentStatus.isEnrolled : false);

  const [dialogState, setDialogState] = useState<EnrollmentDialogState>(defaultDialogState);
  const [hasRequestedEnrollment, setHasRequestedEnrollment] = useState(enrollmentRequested);

  // Check if user is a maintenance member
  const isMaintenance = useMemo(
    () => user?.labels?.some((label) => label.title.toLowerCase() === 'maintenance') ?? false,
    [user?.labels],
  );
  const effectiveStatus: EnrollmentStatus =
    enrollmentStatusProp ?? (hasRequestedEnrollment ? 'requested' : 'not_enrolled');

  const closeDialog = () => setDialogState(EnrollmentDialogState.NONE);
  const handleNavigateToChat = () => {
    const chatUrl = !isDesktop ? '/home?section=chat' : '/home';
    navigate(chatUrl);
  };

  // Determine button behavior based on enrollment status
  const getButtonBehavior = () => {
    switch (effectiveStatus) {
      case 'enrolled':
        return {
          text: translation('program_details_cta'),
          disabled: false,
          onClick: () => setDialogState(EnrollmentDialogState.GO_TO_CHAT),
        };

      case 'requested':
        return {
          text: translation('program_details_cta'),
          disabled: false,
          onClick: () => setDialogState(EnrollmentDialogState.GO_TO_CHAT),
        };

      case 'declined':
        return {
          text: translation('program_details_cta'),
          disabled: false,
          onClick: () => setDialogState(EnrollmentDialogState.GO_TO_CHAT),
        };

      case 'completed':
        return {
          text: 'Previously Completed', // TODO: Add translation
          disabled: true,
          onClick: () => {},
        };

      case 'not_enrolled':
      default: {
        const hasAnyProgramRequested =
          careProgramsData?.some((program) => deriveEnrollmentStatus(program).hasRequestPending) ??
          false;
        if (isMaintenance || hasAnyProgramRequested) {
          return {
            text: translation('program_details_cta'),
            disabled: false,
            onClick: () => setDialogState(EnrollmentDialogState.GO_TO_CHAT),
          };
        }
        return {
          text: translation('request_to_enroll'),
          disabled: false,
          onClick: () => setDialogState(EnrollmentDialogState.CONFIRM_ENROLL),
        };
      }
    }
  };

  const buttonBehavior = getButtonBehavior();
  const handleEnrollButtonClick = buttonBehavior.onClick;

  const handleConfirmEnroll = async () => {
    if (programId) {
      try {
        trackEvent(PendoEventType.REQUEST_TO_ENROLL, {
          eventType: PendoEventType.REQUEST_TO_ENROLL,
          programId: `${programId}`,
        });
        await requestEnrollment({ programId });
        setHasRequestedEnrollment(true);
        setDialogState(EnrollmentDialogState.REQUEST_SENT);
        refetchCarePrograms();
        onRefetchCarePrograms?.();
      } catch (enrollError) {
        if (isFetchBaseQueryError(enrollError) && enrollError.status === 412) {
          setHasRequestedEnrollment(true);
          setDialogState(EnrollmentDialogState.REQUEST_SENT);
          return;
        }
        setDialogState(EnrollmentDialogState.REQUEST_FAILED);
      }
    } else if (onConfirmEnroll) {
      // Fallback to callback
      Promise.resolve(onConfirmEnroll())
        .then(() => {
          setHasRequestedEnrollment(true);
          setDialogState(EnrollmentDialogState.REQUEST_SENT);
        })
        .catch(() => {
          setDialogState(EnrollmentDialogState.REQUEST_FAILED);
        });
    }
  };

  const handleOpenChat = () => {
    trackEvent(PendoEventType.CHAT_WITH_YOUR_CARE_TEAM, {
      eventType: PendoEventType.CHAT_WITH_YOUR_CARE_TEAM,
    });

    closeDialog();

    if (onOpenChat) {
      onOpenChat();
    } else {
      handleNavigateToChat();
    }
  };

  const getItemText = (item: WhatToExpectItem): string => {
    switch (item.type) {
      case 'checkInViewProgress':
        return translation('check_in_view_progress_bullet');
      case 'checkIn':
        return translation('check_in_surveys_bullet');
      case 'educationalArticles':
        return translation('educational_articles_bullet');
      case 'daysOfContent':
        return translation('days_of_content_bullet', { arg1: item.daysCount });
      default:
        return '';
    }
  };

  const getContentPx = () => {
    if (isDesktop) return 7;
    return isMobile ? 6 : 3;
  };
  const contentPx = getContentPx();

  const getContentPt = () => {
    if (isDesktop) return 2;
    return isMobile ? 3 : 13;
  };
  const contentPt = getContentPt();

  const getDialogContentProps = () => {
    switch (dialogState) {
      case EnrollmentDialogState.CONFIRM_ENROLL:
        return {
          className: 'care-program-detail-page__confirm-enroll-dialog',
          title: translation('request_to_enroll_in_this_program'),
          description: translation('request_prompt_alert_message'),
          noButtonLabel: translation('cancel'),
          yesButtonLabel: translation('request'),
          handleNo: closeDialog,
          handleYes: handleConfirmEnroll,
        };
      case EnrollmentDialogState.REQUEST_SENT:
        return {
          className: 'care-program-detail-page__request-sent-dialog',
          title: translation('request_sent'),
          description: translation('request_submitted_alert_message'),
          noButtonLabel: undefined,
          yesButtonLabel: translation('general_ok'),
          handleNo: closeDialog,
          handleYes: closeDialog,
        };
      case EnrollmentDialogState.REQUEST_FAILED:
        return {
          className: 'care-program-detail-page__request-failed-dialog',
          title: translation('request_failed'),
          description: translation('request_submission_error_alert_message'),
          noButtonLabel: translation('cancel'),
          yesButtonLabel: translation('try_again'),
          handleNo: closeDialog,
          handleYes: handleConfirmEnroll,
        };
      case EnrollmentDialogState.GO_TO_CHAT:
        return {
          className: 'care-program-detail-page__go-to-chat-dialog',
          title: translation('go_to_chat_title'),
          description: translation('program_details_go_to_chat_body'),
          noButtonLabel: translation('cancel'),
          yesButtonLabel: translation('open_chat'),
          handleNo: closeDialog,
          handleYes: handleOpenChat,
        };
      default:
        return {
          className: '',
          title: '',
          description: '',
          noButtonLabel: undefined,
          yesButtonLabel: undefined,
          handleNo: closeDialog,
          handleYes: closeDialog,
        };
    }
  };

  const enrollmentDialogProps = {
    hideBackdrop: isDesktop,
    ...(isDesktop && {
      PaperProps: {
        elevation: 24,
      },
    }),
  };

  const showChatIcon =
    effectiveStatus === 'enrolled' ||
    effectiveStatus === 'requested' ||
    effectiveStatus === 'declined' ||
    (effectiveStatus === 'not_enrolled' && isMaintenance) ||
    (effectiveStatus === 'not_enrolled' &&
      (careProgramsData?.some((program) => deriveEnrollmentStatus(program).hasRequestPending) ??
        false));

  return {
    isLoading,
    error,
    refetch: refetchCarePrograms,
    selectedProgram,
    programDetail,
    isDetailModalOpen: isModalOpen,
    enrollmentStatus,
    handleProgramClick: openProgram,
    handleModalClose: closeModal,
    dialogState,
    hasRequestedEnrollment,
    showChatIcon,
    contentPx,
    contentPt,
    isDesktop,
    closeDialog,
    handleEnrollButtonClick,
    handleConfirmEnroll,
    handleOpenChat,
    getItemText,
    getDialogContentProps,
    enrollmentDialogProps,
    buttonText: buttonBehavior.text,
    buttonDisabled: buttonBehavior.disabled,
    isSubmitting,
  };
};
